import { CheckOutlined, CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Form, Input, Radio, Select, Typography } from 'antd';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionTargetReference } from '../../types/actionTypes';
import { RTU } from '../../types/RTU';
import ActionModalShell from './ActionModalShell';
import { ActionUploadProgress } from './ActionUploadProgress';
import CompactActionFilePicker from './CompactActionFilePicker';
import { RTU_FILE_LIST_CREDENTIALS_UNAVAILABLE, useRtuFileList } from './useRtuFileList';

export type DownloadKind = 'config' | 'debugLog' | 'deployment';

interface UploadActionModalProps {
  busy?: boolean;
  description: string;
  extension: string;
  onCancel: () => void;
  onLaunch: (file: File, comment: string, run: boolean) => void;
  open: boolean;
  /** When false, hides the "run after upload" option (licence upload has no run param). Default true. */
  showRun?: boolean;
  title: string;
  uploadPercent?: number | null;
}

export const UploadActionModal = ({ busy = false, description, extension, onCancel, onLaunch, open, showRun = true, title, uploadPercent }: UploadActionModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [run, setRun] = useState(true);
  const [fileError, setFileError] = useState<string | null>(null);
  const reset = () => {
    setFile(null);
    setComment('');
    setRun(true);
    setFileError(null);
  };
  useEffect(() => {
    if (!open) reset();
  }, [open]);
  const clear = () => {
    if (busy) return;
    reset();
    onCancel();
  };
  return <ActionModalShell busy={busy} footer={<div className="dms-action-modal__footer"><Button disabled={busy} onClick={clear}>{t('actionsModal.cancel')}</Button><Button type="primary" disabled={!file} loading={busy} onClick={() => file && onLaunch(file, comment.trim(), showRun ? run : false)}>{t('actionsModal.start')}</Button></div>} onCancel={clear} open={open} title={title}>
    <div className="dms-action-modal__stack">
      <p className="dms-action-modal__target-summary">{description}</p>
      <CompactActionFilePicker accept={extension} disabled={busy} file={file} hint={t('actionsModal.fileHint', { extension })} label={t('actionsModal.selectFile')} onChange={(nextFile) => { setFileError(null); setFile(nextFile); }} onInvalid={() => setFileError(t('actionsModal.invalidFile', { extension }))} />
      {fileError ? <Alert role="alert" showIcon type="error" message={fileError} /> : null}
      <ActionUploadProgress busy={busy} uploadPercent={uploadPercent} />
      {showRun ? <Checkbox checked={run} onChange={(event) => setRun(event.target.checked)}>{t('actionsModal.runAfterUpload')}</Checkbox> : null}
      <Input.TextArea aria-label={t('actionsModal.comment')} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={250} showCount value={comment} onChange={(event) => setComment(event.target.value)} placeholder={t('actionsModal.commentPlaceholder')} />
    </div>
  </ActionModalShell>;
};

const SupportReference = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      const pending = navigator.clipboard?.writeText(id);
      if (pending) {
        void Promise.resolve(pending).then(() => setCopied(true), () => undefined);
      }
    } catch {
      // Clipboard unavailable: the reference stays visible for manual copy.
    }
  };
  return <div className="dms-action-modal__support-reference">
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('actionsModal.download.supportReference', { id })}</Typography.Text>
    <Button
      type="text"
      size="small"
      aria-label={t('actionsModal.download.copyReference')}
      title={copied ? t('actionsModal.download.copied') : t('actionsModal.download.copyReference')}
      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
      onClick={copy}
    />
  </div>;
};

export type DownloadSelection = 'CURRENT' | 'EXISTING' | 'LATEST';

interface DownloadActionModalProps {
  busy?: boolean;
  description: string;
  kind: DownloadKind;
  onCancel: () => void;
  onLaunch: (input: { comment: string; filename?: string; save?: boolean; selection?: DownloadSelection }) => void;
  open: boolean;
  targets?: RTU[];
  title: string;
}

const defaultSelectionFor = (kind: DownloadKind): DownloadSelection => kind === 'deployment' ? 'LATEST' : 'CURRENT';

export const DownloadActionModal = ({ busy = false, description, kind, onCancel, onLaunch, open, targets = [], title }: DownloadActionModalProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [save, setSave] = useState(false);
  const [selection, setSelection] = useState<DownloadSelection>(defaultSelectionFor(kind));
  const [filename, setFilename] = useState<string | undefined>(undefined);
  const reset = () => {
    setComment('');
    setSave(false);
    setSelection(defaultSelectionFor(kind));
    setFilename(undefined);
  };
  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const actionTargets = useMemo<ActionTargetReference[]>(
    () => targets
      .map((target) => ({ rtuId: target.id, serialNo: String(target.serialNo ?? '').trim() }))
      .filter((target) => target.rtuId > 0 && Boolean(target.serialNo)),
    [targets],
  );
  const versionedDownload = kind === 'deployment' || kind === 'config';
  const isExistingFlow = selection === 'EXISTING';
  const singleTargetOk = targets.length === 1;
  const fileListKind = kind === 'deployment' ? 'deployment' : 'config';
  const { error: filesError, errorCode: filesErrorCode, files, load: loadFiles, requestId: filesRequestId, state: filesState } = useRtuFileList(
    fileListKind,
    actionTargets,
    open && versionedDownload && isExistingFlow && singleTargetOk,
  );

  const invalidSelection = versionedDownload && isExistingFlow && (
    !filename || filesState !== 'ready' || !files.includes(filename)
  );
  const close = () => { if (!busy) { reset(); onCancel(); } };
  const noFilesFound = filesState === 'ready' && files.length === 0;
  const fileFieldLabel =kind === 'config' ? t('actionsModal.download.configFile') : t('actionsModal.download.deploymentFile');

  return <ActionModalShell busy={busy} footer={<div className="dms-action-modal__footer"><Button disabled={busy} onClick={close}>{t('actionsModal.cancel')}</Button><Button type="primary" loading={busy} disabled={invalidSelection} onClick={() => onLaunch({
    comment: comment.trim(),
    ...(kind === 'config' && selection === 'CURRENT' ? { save } : {}),
    ...(versionedDownload ? { selection, ...(isExistingFlow ? { filename } : {}) } : {}),
  })}>{t('actionsModal.startDownload')}</Button></div>} onCancel={close} open={open} title={title}>
    <div className="dms-action-modal__stack">
      <p className="dms-action-modal__target-summary">{description}</p>
      <Alert type="info" showIcon message={kind === 'debugLog' ? t('actionsModal.download.debugLogNotice') : kind === 'config' ? t('actionsModal.download.configNotice') : t('actionsModal.download.deploymentNotice')} />
      {kind === 'config' && selection === 'CURRENT' ? <Checkbox checked={save} onChange={(event) => setSave(event.target.checked)}>{t('actionsModal.download.saveConfig')}</Checkbox> : null}
      {versionedDownload ? <Form layout="vertical">
        <Form.Item label={kind === 'config' ? t('actionsModal.download.configSelection') : t('actionsModal.download.deploymentSelection')}>
          <Radio.Group value={selection} onChange={(event) => { setSelection(event.target.value); setFilename(undefined); }}>
            <Radio value={kind === 'config' ? 'CURRENT' : 'LATEST'}>{kind === 'config' ? t('actionsModal.download.latestConfig') : t('actionsModal.download.latest')}</Radio>
            <Radio value="EXISTING" disabled={!singleTargetOk}>{kind === 'config' ? t('actionsModal.download.existingConfig') : t('actionsModal.download.existingDeployment')}</Radio>
          </Radio.Group>
        </Form.Item>
        {!singleTargetOk ? <Typography.Text type="secondary">{t('actionsModal.download.singleTargetRequired')}</Typography.Text> : null}
        {isExistingFlow && singleTargetOk ? <Form.Item label={fileFieldLabel} required>
          {filesState === 'idle' ? (
            <div className="dms-action-modal__file-load">
              <Typography.Text type="secondary">{t('actionsModal.download.filesLoadNotice')}</Typography.Text>
              <Button onClick={loadFiles} disabled={busy}>{t('actionsModal.download.loadFiles')}</Button>
            </div>
          ) : (
            <div className="dms-action-modal__file-select">
              {noFilesFound ? (
                <div>
                  <Typography.Text type="secondary">{kind === 'config' ? t('actionsModal.download.noConfigurationFiles') : t('actionsModal.download.noDeploymentFiles')}</Typography.Text>
                  {kind === 'config' ? (
                    <div className="dms-action-modal__file-hint" style={{ marginTop: 8 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}><InfoCircleOutlined /> {t('actionsModal.download.currentConfigurationHint', { option: t('actionsModal.download.latestConfig') })}</Typography.Text>
                    </div>
                  ) : null}
                </div>
              ) : <Select
                aria-label={fileFieldLabel}
                value={filename}
                onChange={setFilename}
                loading={filesState === 'loading'}
                disabled={filesState === 'loading'}
                notFoundContent={filesState === 'loading' ? t('actionsModal.download.filesLoading') : filesState === 'error' ? t('actionsModal.download.filesLoadFailed') : t('actionsModal.download.filesEmpty')}
                options={files.map((file) => ({ label: file, value: file }))}
                showSearch
                style={{ width: '100%' }}
                virtual={false}
              />}
              {filesState === 'ready' ? (
                <Typography.Text type="secondary" className="dms-action-modal__file-count">
                  {!noFilesFound ? <><span>{t('actionsModal.download.filesCount', { count: files.length })}</span>{' · '}</> : null}
                  <Button type="link" size="small" onClick={loadFiles}>{t('actionsModal.download.reload')}</Button>
                </Typography.Text>
              ) : null}
              {filesState === 'error' ? (
                <>
                  <Alert
                    type="error"
                    showIcon
                    message={filesError}
                    action={filesErrorCode === RTU_FILE_LIST_CREDENTIALS_UNAVAILABLE ? undefined : <Button size="small" onClick={loadFiles}>{t('actionsModal.download.retry')}</Button>}
                  />
                  {filesRequestId ? <SupportReference id={filesRequestId} /> : null}
                </>
              ) : null}
            </div>
          )}
        </Form.Item> : null}
      </Form> : null}
      <Input.TextArea aria-label={t('actionsModal.comment')} autoSize={{ minRows: 3, maxRows: 6 }} maxLength={250} showCount value={comment} onChange={(event) => setComment(event.target.value)} placeholder={t('actionsModal.commentPlaceholder')} />
    </div>
  </ActionModalShell>;
};

interface QuickActionsModalProps {
  children: ReactNode;
  onClose: () => void;
  open: boolean;
}

export const QuickActionsModal = ({ children, onClose, open }: QuickActionsModalProps) => {
  const { t } = useTranslation();
  return <ActionModalShell footer={<Button onClick={onClose}>{t('actionsModal.close')}</Button>} onCancel={onClose} open={open} title={t('actionsModal.quickActions.title')} width={480}>{children}</ActionModalShell>;
};
